import { FLOW_GRAPH, FLOW_IDS, IChatMessage } from 'ai-assistant/models/chat-message';
import { EmulatedFlowCompletionResponse, IStreamingCompletionResponse, SimulatedStreamingResponse } from 'ai-assistant/models/completion-response';
import { CompletionService, ICompletionService } from 'ai-assistant/services/completion-service';
import { UUID } from 'crypto';
import { useMemo, useState } from 'react';
import useChatAnimationCoordinator from './useChatAnimationCoordinator';
import { AuthenticatedAccount } from '@client:common/AuthenticatedAccount';
import React from 'react';
import useSession from '@client:common/hooks/useSession';
import { FeedbackService, IFeedbackService } from '../services/feedback-service';
import { GlobalStore } from '@client:common/global-store';
import ReactGA from "react-ga4";

export enum AssistantChatStatus {
  IDLE,
  SUBMITTED,
  STREAMING,
  ERROR,
}

export type Feedback = {
  type: 'positive' | 'negative';
  feedbackEntryUUID?: UUID;
  isSubmitted: boolean;
};
const MAX_GUEST_QUESTIONS = 5;
const GUEST_KEY = 'guestAssistantQuestions';

export default function useAssistantChat() {
  const coordinator = useChatAnimationCoordinator();
  const { ensureAuthenticated } = useSession();

  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [isOpen, setIsOpen] = useState(false);

  const completionService = useMemo<ICompletionService>(
    () => new CompletionService(sessionId),
    // () => new MockCompletionService(),
    [sessionId]
  );
  const feedbackService = useMemo<IFeedbackService>(() => new FeedbackService(), []);

  const [messages, setMessages] = useState<IChatMessage[]>(getInitialMessages());
  const [feedback, setFeedback] = useState<Record<UUID, Feedback | null>>({});

  const [input, setInput] = useState<string>('');
  const [status, setStatus] = useState<AssistantChatStatus>(AssistantChatStatus.IDLE);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value)
  };

  const _streamCompletionResponse = (completionResponse: IStreamingCompletionResponse, updatingChatId: UUID) => {
    setStatus(AssistantChatStatus.STREAMING);

    completionResponse.onChunk((chunk: string) => {
      setMessages(prev =>
        prev.map(msg =>
          msg.id === updatingChatId ? { ...msg, content: msg.content + chunk } : msg
        )
      );
    }).then((finalContent: string) => {
      setStatus(AssistantChatStatus.IDLE);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === updatingChatId ? { ...msg, content: finalContent } : msg
        )
      );
    }).catch((error) => {
      console.error("useAssistantChat: _streamCompletionResponse", error);
      setStatus(AssistantChatStatus.ERROR);
    });
  }

  const handleSubmit = async (nextMessageId: UUID) => {
    const currentInput = input;
    setInput('');

    coordinator.withNextRender(() => {
      setInput('');
    });

    if (status !== AssistantChatStatus.IDLE && status !== AssistantChatStatus.ERROR) {
      return;
    }

    if (currentInput.trim() === '') return;

    const userMessage: IChatMessage = {
      id: nextMessageId,
      createdAt: new Date().toISOString(),
      type: 'text',
      role: 'user',
      content: currentInput
    };

    setMessages(prev => [...prev, userMessage]);
    setStatus(AssistantChatStatus.SUBMITTED);

    await coordinator.userMessageAnimationDidFinish();

    const authenticatedAccount: AuthenticatedAccount | null = GlobalStore.getInstance().valueOf('authenticatedAccount');


    if (!authenticatedAccount) {
      const guestQuestions = parseInt(localStorage.getItem(GUEST_KEY) || '0', 10);

      if (guestQuestions >= MAX_GUEST_QUESTIONS) {
        const loginPromptMessage: IChatMessage = {
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          type: 'text',
          role: 'assistant',
          content: `You have reached the limit of ${MAX_GUEST_QUESTIONS} questions available for guest users. To continue using the AI Assistant, please create an account or log in to your existing account.`
        };
        setMessages(prev => [...prev, loginPromptMessage]);
        setStatus(AssistantChatStatus.IDLE);
        return;
      } else {
        localStorage.setItem(GUEST_KEY, (guestQuestions + 1).toString());
      }
    }

    if (authenticatedAccount && !authenticatedAccount.isVerified) {
      const verificationMessage: IChatMessage = {
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        type: 'text',
        role: 'assistant',
        content: ''
      };
      setMessages(prev => [...prev, verificationMessage]);

      const verificationContent = "Your account is not verified. Check your email to verify your account then refresh the page to use the AI Assistant.";
      const response = new SimulatedStreamingResponse(verificationContent);
      _streamCompletionResponse(response, verificationMessage.id);

      setStatus(AssistantChatStatus.IDLE);
      return;
    }

    const messagesForApi = [...messages, userMessage];

    const assistantNewMessage: IChatMessage = {
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      type: 'text',
      role: 'assistant',
      content: ''
    };

    setMessages(prev => [...prev, assistantNewMessage]);

    try {
      const accessToken = authenticatedAccount?.accessToken || '';
      const response = await completionService.completion(messagesForApi, accessToken);
      _streamCompletionResponse(response, assistantNewMessage.id);
    } catch (apiError) {
      console.error("Completion API error:", apiError);
      setMessages(prev => prev.filter(msg => msg.id !== assistantNewMessage.id));
      setStatus(AssistantChatStatus.ERROR);
      const errorDisplayMessage: IChatMessage = {
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        type: 'text',
        role: 'assistant',
        content: "Sorry, I couldn't process your request at this time. Please try again later."
      };
      setMessages(prev => [...prev, errorDisplayMessage]);
    }
  };

  const handleFlowSelection = async (flowId: UUID, selectionIndex: number, nextMessageId: UUID) => {
    try {
      if (status !== AssistantChatStatus.IDLE && status !== AssistantChatStatus.ERROR) {
        return;
      }

      const selectedOption = FLOW_GRAPH?.[flowId]?.options?.[selectionIndex];

      if (!selectedOption) {
        throw new Error(`Flow ${flowId} not found or has no option at index ${selectionIndex}`);
      }
      const userMessage: IChatMessage = {
        id: nextMessageId,
        createdAt: new Date().toISOString(),
        type: 'flow',
        role: 'user',
        flowId_selectionIndex: [flowId, selectionIndex],
        content: selectedOption.text
      }
      setMessages(prev => [...prev, userMessage]);
      setStatus(AssistantChatStatus.SUBMITTED);
      ReactGA.event({
        category: "AI Assistant",
        action: `suggested_chat_option_selected_${selectedOption.customEvent || 'unknown_suggested_chat_option_selected'}`,
        label: `Selected a suggested chat option: ${selectedOption.text}`
      })
      await coordinator.flowMessageAnimationDidFinish();

      const authenticatedAccount: AuthenticatedAccount | null = GlobalStore.getInstance().valueOf('authenticatedAccount');

      if (!authenticatedAccount) {
        const guestQuestions = parseInt(localStorage.getItem(GUEST_KEY) || '0', 10);

        if (guestQuestions >= MAX_GUEST_QUESTIONS) {
          const loginPromptMessage: IChatMessage = {
            id: crypto.randomUUID(),
            createdAt: new Date().toISOString(),
            type: 'text',
            role: 'assistant',
            content: `You have reached the limit of ${MAX_GUEST_QUESTIONS} questions available for guest users. To continue using the AI Assistant, please create an account or log in to your existing account.`
          };
          setMessages(prev => [...prev, loginPromptMessage]);
          setStatus(AssistantChatStatus.IDLE);
          return;
        } else {
          localStorage.setItem(GUEST_KEY, (guestQuestions + 1).toString());
        }
      }

      const newMessage: IChatMessage = {
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        type: 'flow',
        role: 'assistant',
        flowId: selectedOption.nextNodeId,
        content: ''
      }
      setMessages(prev => [...prev, newMessage]);
      const completionResponse = new EmulatedFlowCompletionResponse(newMessage.flowId);
      _streamCompletionResponse(completionResponse, newMessage.id);
    } catch (e: any) {
      setStatus(AssistantChatStatus.ERROR);
      console.error(e);
      const errorDisplayMessage: IChatMessage = {
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        type: 'text',
        role: 'assistant',
        content: "Sorry, I encountered an issue. Please try again."
      };
      setMessages(prev => [...prev, errorDisplayMessage]);
    }
  }

  const handleReset = () => {
    setMessages(getInitialMessages());
    setStatus(AssistantChatStatus.IDLE);
    setInput('');
    setSessionId(crypto.randomUUID());
    setFeedback({});
  };

  const handleFeedbackSubmit = async (messageId: UUID, feedbackType: 'positive' | 'negative', userFeedbackText?: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message) {
      console.error('Message not found for feedback submission:', messageId);
      return;
    }

    const previousFeedback = feedback[messageId];
    setFeedback(prev => ({
      ...prev,
      [messageId]: {
        type: feedbackType,
        feedbackEntryUUID: previousFeedback?.feedbackEntryUUID,
        isSubmitted: false
      },
    }));

    try {
      const authenticatedAccount = await ensureAuthenticated();
      const feedbackEntryUUID = await feedbackService.submitFeedback({
        feedbackType,
        sessionId,
        queryText: message.content,
        accessToken: authenticatedAccount.accessToken,
        feedbackEntryUUID: previousFeedback?.feedbackEntryUUID,
        userFeedbackText
      });

      setFeedback(prev => ({
        ...prev,
        [messageId]: {
          type: feedbackType,
          feedbackEntryUUID,
          isSubmitted: true
        },
      }));
    } catch (error) {
      console.error('Failed to submit feedback through hook:', error);
      setFeedback(prev => ({
        ...prev,
        [messageId]: previousFeedback,
      }));
    }
  };

  return {
    coordinator,
    messages,
    input,
    handleInputChange,
    handleSubmit,
    handleFlowSelection,
    status,
    handleReset,
    handleFeedbackSubmit,
    feedback,
    isOpen,
    toggleAssistant: setIsOpen,
  }
}

// MARK: - Initial Messages

const getInitialMessages = (): IChatMessage[] => ([
  {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    type: 'text',
    role: 'system',
    content: `
By interacting with MSP's AI
Assistant, you agree to the [Security & Privacy Policy](https://www.pnnl.gov/security-privacy). Your information will remain private and will not be shared without your permission.

**Note:** This assistant is a beta feature and may occasionally provide inaccurate information. Please verify responses as needed.
      `
  },
  {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    type: 'flow',
    role: 'assistant',
    content: 'Hello! I\'m MSP\'s AI Assistant.\n\nHow can I help you today?',
    flowId: FLOW_IDS.INITIAL_FLOW_ID
  }
])
